import React, { useEffect, useState } from "react";
import { supabase } from "@/supabase/client";
import { Icon } from "@iconify/react";
import { ScrollArea, ScrollBar } from "@/components/ui/custom/ui/scroll-area";
const MAX_GOLD = 7000;
const MAX_RUBY = 1500;
const MAX_SAPPHIRE = 1500;
const MAX_TOTAL = 10000;

function round(val) {
  return Math.round(val);
}

export default function MXSValueOverview({
  walletAddress,
  selectedSeasonData,
}) {
  const [highestGoldUser, setHighestGoldUser] = useState(null);
  const [highestRubyUser, setHighestRubyUser] = useState(null);
  const [highestSapphireUser, setHighestSapphireUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      // Determine which table to use based on selectedSeasonData
      let tableName = "seasons_diff";
      if (selectedSeasonData && !selectedSeasonData.is_active) {
        tableName = `seasons_final_diff_snapshot_${selectedSeasonData.id}`;
      }

      // Fetch top holders
      const { data: highestGold } = await supabase
        .from(tableName)
        .select("gold")
        .order("gold", { ascending: false })
        .limit(1)
        .single();
      setHighestGoldUser(highestGold);
      const { data: highestRuby } = await supabase
        .from(tableName)
        .select("ruby")
        .order("ruby", { ascending: false })
        .limit(1)
        .single();
      setHighestRubyUser(highestRuby);
      const { data: highestSapphire } = await supabase
        .from(tableName)
        .select("sapphire")
        .order("sapphire", { ascending: false })
        .limit(1)
        .single();
      setHighestSapphireUser(highestSapphire);
      // Fetch user data
      if (walletAddress) {
        const { data: user, error } = await supabase
          .from(tableName)
          .select("gold, ruby, sapphire, mxs_rank")
          .eq("wallet_lc", walletAddress.toLowerCase())
          .single();
        setUserData(user);
      } else {
        setUserData(null);
      }
      // Fetch total users
      const { count } = await supabase
        .from(tableName)
        .select("*", { count: "exact", head: true });
      setTotalUsers(count);
      setLoading(false);
    };
    fetchData();
  }, [walletAddress, selectedSeasonData]);

  // Calculation logic
  let goldMXS = 0,
    rubyMXS = 0,
    sapphireMXS = 0,
    totalMXS = 0;
  let goldPct = 0,
    rubyPct = 0,
    sapphirePct = 0,
    totalPct = 0;
  if (userData && highestGoldUser && highestRubyUser && highestSapphireUser) {
    goldMXS = (userData.gold / highestGoldUser.gold) * MAX_GOLD;
    rubyMXS = (userData.ruby / highestRubyUser.ruby) * MAX_RUBY;
    sapphireMXS =
      (userData.sapphire / highestSapphireUser.sapphire) * MAX_SAPPHIRE;
    totalMXS = goldMXS + rubyMXS + sapphireMXS;
    // Contribution percentage of each resource to total MXS
    goldPct = totalMXS ? Math.round((goldMXS / totalMXS) * 100) : 0;
    rubyPct = totalMXS ? Math.round((rubyMXS / totalMXS) * 100) : 0;
    sapphirePct = totalMXS ? Math.round((sapphireMXS / totalMXS) * 100) : 0;
    totalPct = Math.round((totalMXS / MAX_TOTAL) * 100);
  }

  // Replace ProgressCircle with MultiSegmentProgressCircle
  function MultiSegmentProgressCircle({
    goldPct,
    rubyPct,
    sapphirePct,
    totalMXS,
  }) {
    const r = 60;
    const c = 2 * Math.PI * r;
    const cx = 75,
      cy = 75;

    // Calculate the arc lengths for each segment
    const goldLen = (goldPct / 100) * c;
    const rubyLen = (rubyPct / 100) * c;
    const sapphireLen = (sapphirePct / 100) * c;

    // Start offsets for each segment
    const goldOffset = 0;
    const rubyOffset = goldOffset + goldLen;
    const sapphireOffset = rubyOffset + rubyLen;

    // Helper to get label position for each segment
    function getLabelPos(startPct, lenPct) {
      // Find the angle in radians for the center of the arc
      const angle = ((startPct + lenPct / 2) / 100) * 2 * Math.PI - Math.PI / 2;
      const labelR = r - 24; // slightly inside the arc
      return {
        x: cx + labelR * Math.cos(angle),
        y: cy + labelR * Math.sin(angle) + 5, // +5 to center vertically
      };
    }

    let runningPct = 0;
    const goldLabel = getLabelPos(runningPct, goldPct);
    runningPct += goldPct;
    const rubyLabel = getLabelPos(runningPct, rubyPct);
    runningPct += rubyPct;
    const sapphireLabel = getLabelPos(runningPct, sapphirePct);

    return (
      <div className="flex flex-col items-center w-full">
        <svg width="150" height="150" className="mx-auto block">
          <defs>
            <linearGradient id="gold-gradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#B8860B" />
              <stop offset="100%" stopColor="#FFD700" />
            </linearGradient>
            <linearGradient id="ruby-gradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#8B1A1A" />
              <stop offset="100%" stopColor="#E53935" />
            </linearGradient>
            <linearGradient id="sapphire-gradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#0D1A4A" />
              <stop offset="100%" stopColor="#1E88E5" />
            </linearGradient>
          </defs>
          {/* Background circle */}
          <circle
            cx={cx}
            cy={cy}
            r={r}
            stroke="#52525b"
            strokeWidth="16"
            fill="none"
          />
          {/* Gold segment with gradient */}
          {goldPct > 0 && (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              stroke="url(#gold-gradient)"
              strokeWidth="16"
              fill="none"
              strokeDasharray={`${goldLen} ${c - goldLen}`}
              strokeDashoffset={-goldOffset}
              strokeLinecap="round"
            />
          )}
          {/* Ruby segment with gradient */}
          {rubyPct > 0 && (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              stroke="url(#ruby-gradient)"
              strokeWidth="16"
              fill="none"
              strokeDasharray={`${rubyLen} ${c - rubyLen}`}
              strokeDashoffset={-rubyOffset}
              strokeLinecap="round"
            />
          )}
          {/* Sapphire segment with gradient */}
          {sapphirePct > 0 && (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              stroke="url(#sapphire-gradient)"
              strokeWidth="16"
              fill="none"
              strokeDasharray={`${sapphireLen} ${c - sapphireLen}`}
              strokeDashoffset={-sapphireOffset}
              strokeLinecap="round"
            />
          )}
          {/* Center text: total MXS value */}
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            fill="#fff"
            fontWeight="bold"
            dominantBaseline="middle"
            className="text-sm"
          >
            {/* {Math.round(totalMXS)} MXS */}
          </text>
          {/* Gold percent label */}
          {goldPct > 0 && (
            <text
              x={goldLabel.x}
              y={goldLabel.y}
              textAnchor="middle"
              fill="#B8860B"
              fontWeight="bold"
              fontSize="1rem"
              style={{ textShadow: "0 0 2px #222" }}
            >
              {goldPct}%
            </text>
          )}
          {/* Ruby percent label */}
          {rubyPct > 0 && (
            <text
              x={rubyLabel.x}
              y={rubyLabel.y}
              textAnchor="middle"
              fill="#E53935"
              fontWeight="bold"
              fontSize="1rem"
              style={{ textShadow: "0 0 2px #222" }}
            >
              {rubyPct}%
            </text>
          )}
          {/* Sapphire percent label */}
          {sapphirePct > 0 && (
            <text
              x={sapphireLabel.x}
              y={sapphireLabel.y}
              textAnchor="middle"
              fill="#1E88E5"
              fontWeight="bold"
              fontSize="1rem"
              style={{ textShadow: "0 0 2px #222" }}
            >
              {sapphirePct}%
            </text>
          )}
        </svg>
        {/* Legend */}
        <div className="flex gap-4 mt-2 text-xs">
          <div className="flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{
                background: "linear-gradient(135deg, #B8860B 0%, #FFD700 100%)",
              }}
            ></span>
            <span className="text-zinc-300">Gold</span>
          </div>
          <div className="flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{
                background: "linear-gradient(135deg, #8B1A1A 0%, #E53935 100%)",
              }}
            ></span>
            <span className="text-zinc-300">Ruby</span>
          </div>
          <div className="flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{
                background: "linear-gradient(135deg, #0D1A4A 0%, #1E88E5 100%)",
              }}
            ></span>
            <span className="text-zinc-300">Sapphire</span>
          </div>
        </div>
      </div>
    );
  }

  if (loading)
    return <div className="text-center py-12 text-zinc-400">Loading...</div>;

  if (!walletAddress) {
    return null;
  }

  return (
    <div className="flex flex-col md:flex-row gap-6 w-full">
      {/* Left: Table */}
      <div className="flex-1 bg-[#1a1a1a] rounded-2xl border border-zinc-700 p-3 sm:p-6 shadow-lg min-w-[0]">
        <div className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          <Icon icon="ph:math-operations-fill" className="h-6 w-6" />
          MXS Value Overview
        </div>
        <div className="text-zinc-400 mb-2 sm:mb-4 text-left text-sm sm:text-base">
          Your total MXS:{" "}
          <span className="font-bold text-white">{round(totalMXS)} points</span>
        </div>
        <div className="border border-zinc-600 rounded-lg overflow-hidden mb-4">
          <ScrollArea>
            <table className="w-full border-none min-w-[500px] text-xs sm:text-sm">
              <thead>
                <tr className="bg-zinc-700 text-zinc-200 rounded-t-lg font-semibold border-b border-zinc-600">
                  <th className="py-2 px-2 sm:px-3 text-left border-r border-zinc-600 border-t border-l border-zinc-600">
                    Points
                  </th>
                  <th className="py-2 px-2 sm:px-3 text-left border-r border-zinc-600 border-t border-zinc-600">
                    Leader
                  </th>
                  <th className="py-2 px-2 sm:px-3 text-left border-r border-zinc-600 border-t border-zinc-600">
                    Your Points
                  </th>
                  <th className="py-2 px-2 sm:px-3 text-left border-t border-r border-zinc-600">
                    MXS Contribution
                  </th>
                </tr>
              </thead>
              <tbody className="text-zinc-400">
                <tr className="border-b border-zinc-600">
                  <td className="py-2 px-2 sm:px-3 flex items-center font-medium text-left border-r border-zinc-600">
                    <img
                      src="https://www.metaxseed.io/_next/image?url=https%3A%2F%2Fres.cloudinary.com%2Fdot2onqdb%2Fimage%2Fupload%2Fv1729068489%2Fgold_lr_h4yg2m.png&w=640&q=75"
                      alt="Gold"
                      className="w-5 h-5 sm:w-6 sm:h-6 rounded mr-2"
                    />{" "}
                    Gold <span className="text-zinc-400 ml-4">|</span>
                    <a
                      href="/missions"
                      className="ml-2 px-2 py-1 text-xs text-blue-600 underline hover:text-blue-800 transition"
                    >
                      Get More
                      <svg
                        className="inline ml-1"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </a>
                  </td>
                  <td className="py-2 px-2 sm:px-3 text-left border-r border-zinc-600">
                    {highestGoldUser?.gold?.toLocaleString() ?? "-"}
                  </td>
                  <td className="py-2 px-2 sm:px-3 font-bold text-left border-r border-zinc-600">
                    {userData?.gold?.toLocaleString() ?? "-"}
                  </td>
                  <td className="py-2 px-2 sm:px-3 text-left">
                    {round(goldMXS)}{" "}
                    <span className="text-zinc-400 text-xs">({goldPct}%)</span>
                  </td>
                </tr>
                <tr className="border-b border-zinc-600">
                  <td className="py-2 px-2 sm:px-3 flex items-center font-medium text-left border-r border-zinc-600">
                    <img
                      src="https://cqfjxolodeqrlvaifybm.supabase.co/storage/v1/object/public/image_gems//gem_4251687.png"
                      alt="Ruby"
                      className="w-5 h-5 sm:w-6 sm:h-6 rounded mr-2"
                    />{" "}
                    Ruby <span className="text-zinc-400 ml-4">|</span>
                    <a
                      href="https://lfj.gg/avalanche/swap?inputCurrency=AVAX&outputCurrency=0x0A10D108E2d81ccC793E37b56206C84bF96ddc57"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 px-2 py-1 text-xs text-blue-600 underline hover:text-blue-800 transition"
                    >
                      Get More
                      <svg
                        className="inline ml-1"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </a>
                  </td>
                  <td className="py-2 px-2 sm:px-3 text-left border-r border-zinc-600">
                    {highestRubyUser?.ruby?.toLocaleString() ?? "-"}
                  </td>
                  <td className="py-2 px-2 sm:px-3 font-bold text-left border-r border-zinc-600">
                    {userData?.ruby?.toLocaleString() ?? "-"}
                  </td>
                  <td className="py-2 px-2 sm:px-3 text-left">
                    {round(rubyMXS)}{" "}
                    <span className="text-zinc-400 text-xs">({rubyPct}%)</span>
                  </td>
                </tr>
                <tr className="border-b border-zinc-600">
                  <td className="py-2 px-2 sm:px-3 flex items-center font-medium text-left border-r border-zinc-600">
                    <img
                      src="https://cqfjxolodeqrlvaifybm.supabase.co/storage/v1/object/public/image_gems//gem_785126.png"
                      alt="Sapphire"
                      className="w-5 h-5 sm:w-6 sm:h-6 rounded mr-2"
                    />{" "}
                    Sapphire <span className="text-zinc-400 ml-4">|</span>
                    <a
                      href="/nodes"
                      className="ml-2 px-2 py-1 text-xs text-blue-600 underline hover:text-blue-800 transition"
                    >
                      Get More
                      <svg
                        className="inline ml-1"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </a>
                  </td>
                  <td className="py-2 px-2 sm:px-3 text-left border-r border-zinc-600">
                    {highestSapphireUser?.sapphire?.toLocaleString() ?? "-"}
                  </td>
                  <td className="py-2 px-2 sm:px-3 font-bold text-left border-r border-zinc-600">
                    {userData?.sapphire?.toLocaleString() ?? "-"}
                  </td>
                  <td className="py-2 px-2 sm:px-3 text-left">
                    {round(sapphireMXS)}{" "}
                    <span className="text-zinc-400 text-xs">
                      ({sapphirePct}%)
                    </span>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="bg-zinc-700 text-zinc-200 font-semibold border-t border-zinc-600">
                  <td className="py-3 px-2 sm:px-3 text-left flex items-center gap-2">
                    <span className="flex items-center gap-2">
                      <img
                        src="https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/branding%2Fmxs_games%2Fv1%2Fwhite%2Fpng%2Fmxs_icon_white.png?alt=media&token=8a7e36ba-954b-46b3-bcb2-559cec885bdf"
                        alt="MXS"
                        className="w-5 h-5 sm:w-6 sm:h-6 rounded mr-2"
                      />
                      Total MXS
                    </span>
                  </td>
                  <td className="py-3 px-2 text-left border border-zinc-600"></td>
                  <td className="py-3 px-2 text-left border border-zinc-600"></td>
                  <td className="py-3 px-2 sm:px-3 font-bold text-base sm:text-lg text-white text-start border border-zinc-600">
                    {round(totalMXS)}
                  </td>
                </tr>
              </tfoot>
            </table>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
        <div className="text-xs text-zinc-400 text-left mt-2">
          • MXS contribution is calculated using the formula: (Your Value /
          Leader Value) × Multiplier
          <br />• Gold multiplier: 7,000 | Ruby &amp; Sapphire multiplier: 1,500
        </div>
      </div>
      {/* Right: Circular Progress and Stats */}
      <div className="w-full md:w-[340px] bg-[#1a1a1a] rounded-2xl border border-zinc-700 p-4 sm:p-6 shadow-lg flex flex-col items-center justify-center mt-2 md:mt-0">
        <div className="text-lg sm:text-xl font-bold text-white mb-2 text-left w-full">
          <div className="flex items-center justify-start text-left gap-2">
            <Icon icon="garden:line-graph-fill-12" className="h-6 w-6" />
            Total MXS
          </div>
        </div>
        <div className="text-zinc-400 mb-2 sm:mb-4 text-left w-full text-sm sm:text-base">
          Your season performance
        </div>
        <MultiSegmentProgressCircle
          goldPct={goldPct}
          rubyPct={rubyPct}
          sapphirePct={sapphirePct}
          totalMXS={totalMXS}
        />
        <div className="flex justify-between w-full mt-4 text-zinc-400 text-xs sm:text-sm">
          <div>Rank</div>
          <div className="text-right">Total Users</div>
        </div>
        <div className="flex justify-between w-full items-center mb-4">
          <div className="text-white font-bold text-base sm:text-lg">
            #{userData?.mxs_rank ?? "-"}
          </div>
          <div className="text-white font-bold text-base sm:text-lg">
            {totalUsers?.toLocaleString() ?? "-"}
          </div>
        </div>
        <div className="flex justify-between w-full mt-2 bg-zinc-700 rounded-lg p-2 sm:p-3">
          <div className="text-zinc-400 text-xs sm:text-sm flex items-center gap-2">
            <img
              src="/assets/images/logo/mxs_logo_icon.svg"
              alt="MXS Icon"
              className="w-4 h-4 sm:w-5 sm:h-5 mr-1"
            />
            Total Available MXS
          </div>
          <div className="text-white font-bold text-base sm:text-lg">
            10,000
          </div>
        </div>
      </div>
    </div>
  );
}
